import { useState } from 'react';
import { Link } from 'react-router-dom';

type Engagement = 'sans' | '6mois' | '12mois';

const ECOMMERCE_PLANS = [
  {
    name: 'Essentiel',
    prices: { sans: 149, '6mois': 119, '12mois': 99 },
    color: 'teal',
    icon: 'ri-store-line',
    popular: false,
    badge: null,
    description: 'Lancez votre boutique rapidement',
    features: [
      "Boutique en ligne (jusqu'à 10 produits)",
      'Design sur mesure',
      'Optimisation mobile',
      'SEO de base',
      'Paiement en ligne (Stripe, etc.)',
      'Gestion des commandes',
      'Gestion des stocks',
      'Hébergement inclus',
      'Maintenance + sécurité',
    ],
  },
  {
    name: 'Pro',
    prices: { sans: 249, '6mois': 199, '12mois': 169 },
    color: 'emerald',
    icon: 'ri-rocket-line',
    popular: true,
    badge: '⭐ POPULAIRE',
    description: 'Le meilleur rapport qualité / résultats',
    features: [
      "Boutique (jusqu'à 50 produits)",
      'Design sur mesure',
      'Optimisation mobile',
      'SEO avancé',
      "Tunnel d'achat optimisé",
      'Gestion des commandes',
      'Gestion des stocks',
      'Hébergement inclus',
      'Maintenance + modifications',
    ],
  },
  {
    name: 'Premium',
    prices: { sans: 399, '6mois': 329, '12mois': 279 },
    color: 'amber',
    icon: 'ri-vip-crown-line',
    popular: false,
    badge: '👑 PREMIUM',
    description: 'Boutique complète sans limites',
    features: [
      'Boutique complète (produits illimités)',
      'Design sur mesure avancé',
      'Optimisation mobile',
      'SEO avancé',
      'Tunnel de vente optimisé conversion',
      'Gestion des commandes',
      'Gestion des stocks',
      'Automatisations avancées (emails, upsell, relances)',
      'Hébergement inclus',
      'Modifications illimitées',
    ],
  },
];

const MOBILE_LABELS: Record<Engagement, string> = {
  sans: 'Libre',
  '6mois': '6 mois',
  '12mois': '12 mois',
};

const DESKTOP_LABELS: Record<Engagement, string> = {
  sans: 'Sans engagement',
  '6mois': 'Engagement 6 mois',
  '12mois': 'Engagement 12 mois',
};

const SAVINGS: Record<Engagement, string | null> = {
  sans: null,
  '6mois': "Économisez jusqu'à 20%",
  '12mois': "Économisez jusqu'à 34%",
};

export default function EcommerceSection() {
  const [engagement, setEngagement] = useState<Engagement>('sans');

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6 bg-white">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8 sm:mb-14">
          <div className="inline-flex items-center space-x-2 bg-emerald-50 border border-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-xs font-bold mb-4">
            <i className="ri-shopping-cart-2-line text-sm"></i>
            <span>SITES E-COMMERCE</span>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-slate-900 mb-2 sm:mb-4">
            Offres{' '}
            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
              Sites E-commerce
            </span>
          </h2>
          <p className="text-sm sm:text-lg text-slate-500 max-w-xl mx-auto">
            Choisissez l'offre adaptée à votre business
          </p>
        </div>

        {/* Toggle engagement — mobile-first */}
        <div className="flex flex-col items-center mb-8 sm:mb-12 gap-3">
          {/* Pill toggle pleine largeur sur mobile */}
          <div className="w-full max-w-sm sm:w-auto">
            <div className="grid grid-cols-3 sm:inline-flex bg-slate-100 rounded-2xl p-1.5 gap-1 w-full sm:w-auto">
              {(['sans', '6mois', '12mois'] as Engagement[]).map((e) => (
                <button
                  key={e}
                  onClick={() => setEngagement(e)}
                  className={`flex-1 sm:flex-none sm:px-6 py-3 sm:py-2.5 rounded-xl font-bold transition-all duration-200 cursor-pointer text-center ${
                    engagement === e
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md'
                      : 'text-slate-600'
                  }`}
                >
                  {/* Label court sur mobile, complet sur desktop */}
                  <span className="block sm:hidden text-xs">{MOBILE_LABELS[e]}</span>
                  <span className="hidden sm:block text-sm">{DESKTOP_LABELS[e]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Badge économies */}
          {SAVINGS[engagement] && (
            <span className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-1.5 rounded-full text-xs font-bold">
              🎉 {SAVINGS[engagement]}
            </span>
          )}
        </div>

        {/* Cards — empilées sur mobile, 3 colonnes sur md+ */}
        <div className="flex flex-col md:grid md:grid-cols-3 gap-5 sm:gap-6">
          {ECOMMERCE_PLANS.map((plan) => {
            const isEmerald = plan.color === 'emerald';
            const isAmber = plan.color === 'amber';

            return (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl sm:rounded-3xl transition-all duration-300 flex flex-col overflow-hidden ${
                  plan.popular
                    ? 'border-2 border-emerald-400 shadow-2xl shadow-emerald-500/15'
                    : isAmber
                    ? 'border-2 border-amber-200 shadow-lg'
                    : 'border-2 border-slate-100 shadow-md'
                }`}
              >
                {/* Badge populaire */}
                {plan.badge && (
                  <div
                    className={`w-full py-2.5 text-center text-xs font-black text-white tracking-wide ${
                      plan.popular
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                        : 'bg-gradient-to-r from-amber-500 to-yellow-500'
                    }`}
                  >
                    {plan.badge}
                  </div>
                )}

                {/* Contenu de la card */}
                <div className="p-5 sm:p-8 flex flex-col flex-1">

                  {/* Icone + titre + desc */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${
                        plan.popular ? 'bg-emerald-100' : isAmber ? 'bg-amber-100' : 'bg-teal-50'
                      }`}
                    >
                      <i
                        className={`${plan.icon} text-xl ${
                          plan.popular ? 'text-emerald-600' : isAmber ? 'text-amber-600' : 'text-teal-600'
                        }`}
                      ></i>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-black text-slate-900 leading-tight">{plan.name}</h3>
                      <p className="text-xs text-slate-400 leading-tight mt-0.5">{plan.description}</p>
                    </div>
                  </div>

                  {/* Prix */}
                  <div className="flex items-baseline gap-1.5 mb-1">
                    <span
                      className={`text-4xl sm:text-5xl font-black ${
                        plan.popular
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent'
                          : isAmber
                          ? 'text-amber-600'
                          : 'text-teal-600'
                      }`}
                    >
                      {plan.prices[engagement]}€
                    </span>
                    <span className="text-sm text-slate-400 font-medium">/mois</span>
                  </div>
                  {engagement !== 'sans' && (
                    <p className="text-xs text-slate-400 line-through mb-4">
                      {plan.prices.sans}€/mois sans engagement
                    </p>
                  )}
                  {engagement === 'sans' && <div className="mb-4" />}

                  {/* Séparateur */}
                  <div className="border-t border-slate-100 mb-4" />

                  {/* Features — 2 colonnes sur mobile pour gagner de l'espace */}
                  <div className="grid grid-cols-1 sm:grid-cols-1 gap-2 mb-6 flex-1">
                    {plan.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div
                          className={`w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${
                            plan.popular ? 'bg-emerald-100' : isAmber ? 'bg-amber-100' : 'bg-teal-50'
                          }`}
                        >
                          <i
                            className={`ri-check-line text-[10px] ${
                              plan.popular ? 'text-emerald-600' : isAmber ? 'text-amber-600' : 'text-teal-600'
                            }`}
                          ></i>
                        </div>
                        <span className="text-xs sm:text-sm text-slate-700 leading-snug">{f}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Link
                    to="/paiement"
                    className={`w-full flex items-center justify-center gap-2 py-3.5 sm:py-4 rounded-xl text-sm font-bold transition-all duration-300 active:scale-95 hover:scale-[1.02] whitespace-nowrap cursor-pointer ${
                      plan.popular
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-lg hover:shadow-emerald-500/30'
                        : isAmber
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:shadow-lg hover:shadow-amber-500/20'
                        : 'bg-teal-50 text-teal-700 border-2 border-teal-200 hover:bg-teal-100'
                    }`}
                  >
                    <span>S&apos;abonner maintenant</span>
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Livraison */}
        <div className="mt-7 flex items-center justify-center gap-2 text-sm text-slate-500 text-center">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-timer-flash-line text-emerald-500 text-lg"></i>
          </div>
          <span>
            Livraison de votre boutique en{' '}
            <strong className="text-slate-900">7 jours maximum</strong> après validation
          </span>
        </div>
      </div>
    </section>
  );
}
